// AVIV Layers - Mapbox layer management for AVIV tile integration

import {
    AROUND_SELECTION_ZOOM_RANGES,
    getWithinSelectionZoomRanges,
    getZoomRanges,
    LEGACY_HOVER_COLOR,
    LEGACY_SELECTED_COLOR,
    MAP_MAX_ZOOM,
    MAP_MIN_ZOOM,
    parseAvivFeatureId,
    TransactionType,
} from './aviv-config';
import { colorscales, createColorStops, NO_DATA_COLOR } from './colorscales';

// City-level boundary types for zoom cap logic
const CITY_BOUNDARY_TYPES = ['AD08', 'AD09'];

// =============================================================================
// Source Sharing - reuse sources for same tiles to reduce requests ~3x
// =============================================================================

const sharedSourceRegistry = new Map();
const layerToTilesKey = new Map();

function getTilesHashKey(tiles, minzoom, maxzoom) {
    return `${tiles.join('|')}:${minzoom}-${maxzoom}`;
}

function getOrCreateSharedSource(map, tiles, minzoom, maxzoom, layerId) {
    const hashKey = getTilesHashKey(tiles, minzoom, maxzoom);

    if (sharedSourceRegistry.has(hashKey)) {
        const entry = sharedSourceRegistry.get(hashKey);
        if (map.getSource(entry.sourceName)) {
            entry.layerIds.add(layerId);
            layerToTilesKey.set(layerId, hashKey);
            return entry.sourceName;
        }
        sharedSourceRegistry.delete(hashKey);
    }

    const sourceName = `${layerId}-source`;
    map.addSource(sourceName, {
        type: 'vector',
        tiles,
        minzoom,
        maxzoom,
    });

    sharedSourceRegistry.set(hashKey, {
        sourceName,
        layerIds: new Set([layerId]),
        tiles,
    });

    layerToTilesKey.set(layerId, hashKey);
    return sourceName;
}

function unregisterLayerFromSource(layerId) {
    const hashKey = layerToTilesKey.get(layerId);

    if (!hashKey) {
        return `${layerId}-source`;
    }

    layerToTilesKey.delete(layerId);

    if (!sharedSourceRegistry.has(hashKey)) {
        return `${layerId}-source`;
    }

    const entry = sharedSourceRegistry.get(hashKey);
    entry.layerIds.delete(layerId);

    if (entry.layerIds.size === 0) {
        sharedSourceRegistry.delete(hashKey);
        return entry.sourceName;
    }

    return null;
}

function removeLayers(layerIds, map) {
    const sourcesToRemove = new Set();

    layerIds.forEach(layerId => {
        if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
            const sourceToRemove = unregisterLayerFromSource(layerId);
            if (sourceToRemove) {
                sourcesToRemove.add(sourceToRemove);
            }
        }
    });

    sourcesToRemove.forEach(sourceId => {
        if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
        }
    });
}

// =============================================================================
// Zoom Range Utilities
// =============================================================================

// Adjust tile layer zoom ranges to be non-overlapping
function adjustZoomRanges(tileLayers, zoomRanges, options = {}) {
    const { useMinZoomForFirst = true, maxZoomCap } = options;
    if (!tileLayers?.length) {
        return [];
    }

    const zoomRangeByType = new Map();
    zoomRanges.forEach(zr => {
        zoomRangeByType.set(zr.avivType, zr);
        if (zr.placeType) {
            zoomRangeByType.set(zr.placeType, zr);
        }
    });

    const sorted = tileLayers
        .filter(layer => zoomRangeByType.has(layer.place?.type))
        .map(layer => ({ layer, zoomRange: zoomRangeByType.get(layer.place.type) }))
        .sort((a, b) => (a.zoomRange.minzoom ?? 0) - (b.zoomRange.minzoom ?? 0));

    return sorted.map(({ layer, zoomRange }, i) => {
        const isFirst = i === 0;
        const isLast = i === sorted.length - 1;
        const nextMinZoom = !isLast ? sorted[i + 1].zoomRange.minzoom : null;

        const minzoom =
            useMinZoomForFirst && isFirst ? MAP_MIN_ZOOM : zoomRange.minzoom ?? MAP_MIN_ZOOM;

        // Only apply maxZoomCap to layers that start BEFORE the cap
        // Layers that start at or after the cap should extend to MAP_MAX_ZOOM
        // Example: if cap is 15, PARC (minzoom 15) should not be capped
        const effectiveMaxZoomCap =
            maxZoomCap && zoomRange.minzoom < maxZoomCap ? maxZoomCap : null;

        const maxzoom =
            zoomRange.maxzoom ??
            (nextMinZoom && nextMinZoom > minzoom ? nextMinZoom : null) ??
            effectiveMaxZoomCap ??
            MAP_MAX_ZOOM;

        return { ...layer, minzoom, maxzoom };
    });
}

// =============================================================================
// Layer Style Helpers
// =============================================================================

function getOutlineColor(layerType) {
    const placeType = layerType.includes('__') ? layerType.split('__')[1] : layerType;

    switch (placeType) {
        case 'AD06':
        case 'subregion':
            return 'rgba(0, 0, 0, 0.08)';
        case 'BILD':
        case 'building':
            return 'rgba(255, 255, 255, 0.7)';
        default:
            return 'rgba(0, 0, 0, 0.1)';
    }
}

function getBorderLineWidthOverrides(layerType) {
    const placeType = layerType.includes('__') ? layerType.split('__')[1] : layerType;

    switch (placeType) {
        case 'AD06':
        case 'subregion':
            return {
                'line-width': {
                    stops: [
                        [7, 1],
                        [9, 3],
                    ],
                },
            };
        case 'STU3':
        case 'iris':
            return {
                'line-width': {
                    stops: [
                        [10, 0.5],
                        [12, 1],
                    ],
                },
            };
        default:
            return null;
    }
}

// =============================================================================
// Base Layer Functions
// =============================================================================

function addTileLayer({
    layerId,
    tiles,
    minzoom,
    maxzoom,
    layerStyle,
    layersArray,
    map,
    beforeLayer,
}) {
    if (map.getLayer(layerId)) {
        return;
    }

    const layerSource = getOrCreateSharedSource(map, tiles, minzoom, maxzoom, layerId);
    map.addLayer({ ...layerStyle, source: layerSource }, beforeLayer);

    if (layersArray) {
        layersArray.push(layerId);
    }
}

function addPriceLayer({
    map,
    placeId,
    layer,
    colorScaleLevels,
    layersArray,
    beforeLayer,
    transactionType = 'sell',
}) {
    const { tiles, name, minzoom, maxzoom } = layer;
    const layerId = `prices-${transactionType}-${name}-${placeId}`;

    addTileLayer({
        layerId,
        tiles,
        minzoom,
        maxzoom,
        layerStyle: {
            id: layerId,
            type: 'fill',
            'source-layer': name,
            paint: {
                'fill-color': createColorStops(colorScaleLevels),
                'fill-outline-color': getOutlineColor(name),
            },
            minzoom,
            maxzoom,
        },
        layersArray,
        map,
        beforeLayer,
    });
}

function addAllPriceLayers({
    map,
    placeId,
    layerCollection,
    layersArray,
    beforeLayer,
    transactionType = 'sell',
}) {
    const layersWithBoundary = layerCollection.filter(layer => layer.boundaryPlace);

    layersWithBoundary.forEach(layer => {
        const colorScalePriceRanges = Object.values(layer.boundaryPlace.colorScale);
        const colorScaleLevels = colorscales[colorScalePriceRanges.length]?.levels || [];

        if (colorScaleLevels.length === 0) {
            return;
        }

        addPriceLayer({
            map,
            placeId,
            layer,
            colorScaleLevels,
            layersArray,
            beforeLayer,
            transactionType,
        });
    });
}

function addPlaceOutlineLayer({ map, placeId, layer, layersArray, beforeLayer }) {
    const { tiles, name, type, minzoom } = layer;
    const layerId = `current-place-outline-${name}-${placeId}`;
    const maxzoom = 22; // Override to get high-res tiles at all zoom levels

    addTileLayer({
        layerId,
        tiles,
        minzoom,
        maxzoom,
        layerStyle: {
            id: layerId,
            type: 'line',
            'source-layer': type,
            paint: {
                'line-color': LEGACY_SELECTED_COLOR,
                'line-width': 3,
                'line-opacity': 1,
            },
            filter: ['==', ['get', 'feature_id'], placeId],
        },
        map,
        layersArray,
        beforeLayer,
    });
}

function addSelectionLayers({
    map,
    layerData,
    selectionType,
    placeId,
    filterExpression,
    layersArray,
    beforeLayer,
    paintOverrides,
}) {
    const { tiles, name, type, minzoom, maxzoom } = layerData;
    const sourceLayer = type || name;
    const layerBaseId = `selection-${selectionType}-${name}-${placeId}`;
    const fillLayerId = `${layerBaseId}-fill`;
    const borderLayerId = `${layerBaseId}-border`;
    const outlineLayerId = `${layerBaseId}-outline`;

    // Transparent fill for click/hover detection
    addTileLayer({
        layerId: fillLayerId,
        tiles,
        minzoom,
        maxzoom,
        layerStyle: {
            id: fillLayerId,
            type: 'fill',
            'source-layer': sourceLayer,
            paint: {
                'fill-color': 'rgba(0, 0, 0, 0)',
                'fill-outline-color': 'rgba(0, 0, 0, 0)',
                'fill-opacity': 0.5,
            },
            minzoom,
            maxzoom,
        },
        layersArray,
        map,
        beforeLayer,
    });

    // White border lines between areas
    addTileLayer({
        layerId: borderLayerId,
        tiles,
        minzoom,
        maxzoom,
        layerStyle: {
            id: borderLayerId,
            type: 'line',
            'source-layer': sourceLayer,
            paint: {
                'line-color': 'rgba(255, 255, 255, 1)',
                'line-opacity': 1,
                'line-width': 1,
                ...paintOverrides,
            },
            minzoom,
            maxzoom,
        },
        layersArray,
        map,
        beforeLayer,
    });

    // Cyan outline for hover highlight
    addTileLayer({
        layerId: outlineLayerId,
        tiles,
        minzoom,
        maxzoom,
        layerStyle: {
            id: outlineLayerId,
            type: 'line',
            'source-layer': sourceLayer,
            paint: {
                'line-width': 3,
                'line-color': LEGACY_HOVER_COLOR,
            },
            minzoom,
            maxzoom,
        },
        layersArray,
        map,
        beforeLayer,
    });

    map.setFilter(outlineLayerId, ['in', 'feature_id', '']);

    if (filterExpression) {
        const allFilters = ['all', filterExpression];
        map.setFilter(fillLayerId, allFilters);
        map.setFilter(borderLayerId, allFilters);
    }
}

function addNoDataFillLayer({ map, layerData, placeId, layersArray, beforeLayer }) {
    const { tiles, name, type, minzoom, maxzoom } = layerData;
    const sourceLayer = type || name;
    const layerId = `geo-fill-${name}-z${minzoom}-${placeId}`;

    addTileLayer({
        layerId,
        tiles,
        minzoom,
        maxzoom,
        layerStyle: {
            id: layerId,
            type: 'fill',
            'source-layer': sourceLayer,
            paint: {
                'fill-color': NO_DATA_COLOR,
                'fill-outline-color': 'rgba(0, 0, 0, 0.1)',
            },
            minzoom,
            maxzoom,
        },
        layersArray,
        map,
        beforeLayer,
    });
}

// Border-only layer: subtle outlines without fill (STU3 colors show through)
function addBorderOnlyLayer({ map, layerData, placeId, layersArray, beforeLayer }) {
    const { tiles, name, type, minzoom, maxzoom } = layerData;
    const sourceLayer = type || name;
    const layerId = `geo-border-${name}-z${minzoom}-${placeId}`;

    addTileLayer({
        layerId,
        tiles,
        minzoom,
        maxzoom,
        layerStyle: {
            id: layerId,
            type: 'line',
            'source-layer': sourceLayer,
            paint: {
                'line-color': 'rgba(0, 0, 0, 0.1)',
                'line-width': 1,
            },
            minzoom,
            maxzoom,
        },
        layersArray,
        map,
        beforeLayer,
    });
}

// =============================================================================
// Exported Layer Management Functions
// =============================================================================

export function addGeoSelectionLayers(
    map,
    geoMetadata,
    currentPlaceId,
    layersArray,
    beforeLayerId = null,
) {
    const adjustedLayers = adjustZoomRanges(geoMetadata, AROUND_SELECTION_ZOOM_RANGES, {
        useMinZoomForFirst: false,
    });

    adjustedLayers.forEach(layer => {
        const paintOverrides = getBorderLineWidthOverrides(layer.type);
        addSelectionLayers({
            map,
            layerData: layer,
            selectionType: 'around',
            placeId: currentPlaceId,
            filterExpression: ['!=', ['get', 'feature_id'], currentPlaceId],
            layersArray,
            beforeLayer: beforeLayerId,
            paintOverrides,
        });
    });
}

export function addPriceLayersFromMetadata(
    map,
    priceMetadata,
    placeId,
    transactionType,
    layersArray,
    beforeLayerId = null,
    boundaryPlaceId = null,
    originalBoundaryPlaceId = null,
) {
    const hasHighZoomPriceData = priceMetadata.some(layer =>
        ['PARC', 'BILD', 'BLOC'].includes(layer?.place?.type),
    );
    const hasSTU3PriceData = priceMetadata.some(layer => layer.place?.type === 'STU3');
    const hasBlocPriceData = priceMetadata.some(layer => layer.place?.type === 'BLOC');

    const priceZoomRanges = getZoomRanges(transactionType, 'priceLayer');
    const boundaryType = boundaryPlaceId ? parseAvivFeatureId(boundaryPlaceId)?.type : null;
    const originalBoundaryType = originalBoundaryPlaceId
        ? parseAvivFeatureId(originalBoundaryPlaceId)?.type
        : boundaryType;
    const isCityLevel = CITY_BOUNDARY_TYPES.includes(originalBoundaryType);
    // Cap zoom at city level when:
    // 1. Has PARC/BILD/BLOC data: STU3 gets capped, parcelles take over (legacy: city-price-iris-geo-parcelles)
    // 2. No STU3 data: AD08 city layer gets capped, grey geo parcelles take over (legacy: city-price-city-geo-parcelles)
    // When has STU3 but no PARC, STU3 stays visible at all zooms (legacy: city-price-iris-geo-no-parcelles)
    const shouldCapZoom =
        transactionType === 'sell' && isCityLevel && (hasHighZoomPriceData || !hasSTU3PriceData);
    const priceAdjustOptions = shouldCapZoom ? { maxZoomCap: 14 } : {};

    const adjustedPriceLayers = adjustZoomRanges(
        priceMetadata,
        priceZoomRanges,
        priceAdjustOptions,
    );

    addAllPriceLayers({
        map,
        placeId,
        layerCollection: adjustedPriceLayers,
        layersArray,
        beforeLayer: beforeLayerId,
        transactionType,
    });

    const effectiveBoundaryType = boundaryType || 'AD02';
    const selectionZoomRanges = getWithinSelectionZoomRanges(
        effectiveBoundaryType,
        transactionType,
    );

    // Cap selection zoom at city level when:
    // 1. Has PARC/BILD/BLOC data: PARC takes over selection
    // 2. No STU3 data: geo parcelles take over selection
    const shouldCapSelectionZoom =
        transactionType === 'sell' && isCityLevel && (hasHighZoomPriceData || !hasSTU3PriceData);
    const selectionAdjustOptions = shouldCapSelectionZoom
        ? { useMinZoomForFirst: true, maxZoomCap: 15 }
        : { useMinZoomForFirst: true };
    const adjustedSelectionLayers = adjustZoomRanges(
        priceMetadata,
        selectionZoomRanges,
        selectionAdjustOptions,
    );

    // For RENT: cap STU3 selection at BLOC minZoom (13) when blocks exist
    const finalSelectionLayers =
        transactionType === 'rent' && hasBlocPriceData
            ? adjustedSelectionLayers.map(layer => {
                  const blocPriceMinZoom = 13;
                  if (
                      layer.place?.type === 'STU3' &&
                      (layer.maxzoom > blocPriceMinZoom || !layer.maxzoom)
                  ) {
                      return { ...layer, maxzoom: blocPriceMinZoom };
                  }
                  return layer;
              })
            : adjustedSelectionLayers;

    // Skip BILD/BLOC - buildings and blocks are not clickable
    const clickableSelectionLayers = finalSelectionLayers.filter(
        l => l.place?.type !== 'BILD' && l.place?.type !== 'BLOC',
    );

    clickableSelectionLayers.forEach(layer => {
        const paintOverrides = getBorderLineWidthOverrides(layer.name);
        addSelectionLayers({
            map,
            layerData: {
                ...layer,
                type: layer.name, // Use name as source-layer
            },
            selectionType: 'within',
            placeId,
            filterExpression: null, // No filter for children
            layersArray,
            beforeLayer: beforeLayerId,
            paintOverrides,
        });
    });

    return adjustedPriceLayers;
}

export function addGeoWithinSelectionLayers(
    map,
    geoMetadata,
    placeId,
    transactionType,
    existingPriceTypes,
    layersArray,
    beforeLayerId = null,
) {
    const cityLevelSelectionConfig = {
        [TransactionType.SELL]: [
            { avivType: 'PARC', minzoom: 14, maxzoom: 15, borderOnly: true },
            { avivType: 'PARC', minzoom: 15, maxzoom: MAP_MAX_ZOOM, borderOnly: false },
        ],
        [TransactionType.RENT]: [],
    };

    const neededLayers = cityLevelSelectionConfig[transactionType] || [];

    // Check if we have STU3 price data - if yes, parent colors should show through
    // If no STU3 data, use grey fill (legacy: city-price-city-geo-parcelles)
    const hasSTU3PriceData = existingPriceTypes.includes('STU3');

    neededLayers.forEach(config => {
        if (existingPriceTypes.includes(config.avivType)) {
            return;
        }

        const geoLayer = geoMetadata.find(l => l.type === config.avivType);
        if (!geoLayer) {
            return;
        }

        const layerData = {
            ...geoLayer,
            name: config.avivType,
            type: config.avivType,
            minzoom: config.minzoom,
            maxzoom: config.maxzoom,
        };

        if (hasSTU3PriceData && config.borderOnly) {
            // Has STU3 data, zoom 14-15: border only, STU3 colors show through
            addBorderOnlyLayer({
                map,
                layerData,
                placeId,
                layersArray,
                beforeLayer: beforeLayerId,
            });
        } else if (hasSTU3PriceData) {
            // Has STU3 data, zoom 15+: selection layers (transparent fill)
            addSelectionLayers({
                map,
                layerData,
                selectionType: 'within',
                placeId,
                filterExpression: null,
                layersArray,
                beforeLayer: beforeLayerId,
            });
        } else if (config.borderOnly) {
            // No STU3 data, zoom 14-15: grey fill only (legacy: city-price-city-geo-parcelles)
            addNoDataFillLayer({
                map,
                layerData,
                placeId,
                layersArray,
                beforeLayer: beforeLayerId,
            });
        } else {
            // No STU3 data, zoom 15+: grey fill + selection layers
            addNoDataFillLayer({
                map,
                layerData,
                placeId,
                layersArray,
                beforeLayer: beforeLayerId,
            });
            addSelectionLayers({
                map,
                layerData,
                selectionType: 'within',
                placeId,
                filterExpression: null,
                layersArray,
                beforeLayer: beforeLayerId,
            });
        }
    });
}

export function addCurrentPlaceOutline(map, placeId, geoLayer, layersArray, beforeLayerId = null) {
    addPlaceOutlineLayer({
        map,
        placeId,
        layer: geoLayer,
        layersArray,
        beforeLayer: beforeLayerId,
    });
}

export function removeAvivLayers(map, layerIds) {
    const toRemove = [];

    layerIds.forEach(layerId => {
        const patterns = [layerId, `${layerId}-fill`, `${layerId}-outline`, `${layerId}-border`];
        patterns.forEach(id => {
            if (map.getLayer(id)) {
                toRemove.push(id);
            }
        });
    });

    removeLayers(toRemove, map);
}

// =============================================================================
// Utility Functions
// =============================================================================

export function getSelectableLayers(priceLayerIds, geoLayerIds) {
    return [...priceLayerIds, ...geoLayerIds].filter(id => id.endsWith('-fill'));
}

export function updateHoverState(map, feature, lastHoverLayerId) {
    if (lastHoverLayerId) {
        const outlineId = lastHoverLayerId.replace(/-fill$/, '-outline');
        if (map.getLayer(outlineId)) {
            map.setFilter(outlineId, ['in', 'feature_id', '']);
        }
    }

    if (feature && feature.properties?.feature_id) {
        const layerId = feature.layer?.id;
        if (!layerId) return null;

        const outlineId = layerId.replace(/-fill$/, '-outline');

        if (map.getLayer(outlineId)) {
            const featureId = feature.properties.feature_id;

            map.setFilter(outlineId, ['in', 'feature_id', featureId]);
            map.moveLayer(outlineId);
            return layerId;
        }
    }

    return null;
}
